import { useId } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { Download, KeyRound } from 'lucide-react';
import { portalQueries } from '@blimo/api-client';
import { useApi, useMe } from '@blimo/api-client/react';
import { formatDate, hasPermission, labelOf, TENANCY_PARTY_ROLE_LABELS, TENANCY_STATUS_FILTER_LABELS } from '@blimo/shared';
import { Button, cn, EmptyState, ErrorState, Label, SearchInput, Select, Skeleton, StatusBadge, ToggleGroup } from '@blimo/ui';
import { PageHeader } from '../components/ui/page-header.jsx';
import { ItemIcon, ItemList } from '../components/ui/section.jsx';
import { Pager } from '../components/ui/pager.jsx';
import { unitTitle } from '../lib/units.js';
import { ForbiddenPage } from './pages.jsx';

const STATUS_OPTIONS = Object.entries(TENANCY_STATUS_FILTER_LABELS).map(([value, label]) => ({ value, label }));

/**
 * Mieter (Bewohnerliste, F-702/F-703; `tenancies.view`): Mietverhältnisse der sichtbaren Einheiten mit Status-,
 * Liegenschafts- und Suchfilter (in der URL), XLSX-Export mit denselben Filtern (`tenancies.export`).
 */
export function TenanciesPage() {
  const api = useApi();
  const { data: me } = useMe();
  const navigate = useNavigate();
  const search = useSearch({ strict: false });
  const propertyId = useId();
  const canView = hasPermission(me, 'tenancies.view');
  const page = search.page ?? 1;
  const filter = { status: search.status ?? 'current', property_id: search.property, search: search.q };
  const query = useQuery({ ...portalQueries.tenancies(api, { page, filter }), enabled: canView });
  const properties = useQuery({ ...portalQueries.properties(api), enabled: canView });
  if (!canView) return <ForbiddenPage />;

  const update = (patch) => navigate({ to: '/mieter', search: (prev) => ({ ...prev, page: undefined, ...patch }), replace: true });
  const lastPage = Math.max(1, query.data?.meta?.last_page ?? 1);
  const rows = query.data?.data ?? [];

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Mieter"
        description="Mietverhältnisse Ihrer Einheiten (Quelle: Buchhaltung der Verwaltung)."
        actions={
          hasPermission(me, 'tenancies.export') && (
            <Button asChild variant="outline">
              <a href={api.portal.tenancies.exportUrl(filter)} download>
                <Download aria-hidden="true" /> Export (XLSX)
              </a>
            </Button>
          )
        }
      />
      <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end print:hidden">
        <ToggleGroup
          label="Mietverhältnisse anzeigen"
          size="default"
          value={filter.status}
          onValueChange={(v) => update({ status: v === 'current' ? undefined : v })}
          options={STATUS_OPTIONS}
        />
        <div className="flex flex-col gap-1">
          <Label htmlFor={propertyId} className="sr-only sm:not-sr-only">
            Liegenschaft
          </Label>
          <Select id={propertyId} value={search.property ?? ''} onChange={(e) => update({ property: e.target.value || undefined })} wrapperClassName="sm:w-56">
            <option value="">Alle Liegenschaften</option>
            {(properties.data ?? []).map((p) => (
              <option key={p.id} value={String(p.id)}>
                {p.name}
              </option>
            ))}
          </Select>
        </div>
        <SearchInput label="Mieter suchen" placeholder="Name oder Konto …" value={search.q ?? ''} onValueChange={(v) => update({ q: v || undefined })} />
      </div>

      {query.error ? (
        <ErrorState error={query.error} onRetry={() => query.refetch()} />
      ) : query.isPending ? (
        <Skeleton className="h-48 w-full" />
      ) : rows.length === 0 ? (
        <EmptyState icon={KeyRound} title="Keine Mietverhältnisse" description="Für die gewählten Filter liegen keine Mietverhältnisse vor." />
      ) : (
        <>
          <ItemList aria-label="Mietverhältnisse" className={cn(query.isPlaceholderData && 'opacity-60')}>
            {rows.map((tenancy) => (
              <li key={tenancy.id} className="flex items-start gap-3 px-4 py-3">
                <ItemIcon icon={KeyRound} />
                <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                  <span className="flex min-h-8 flex-wrap items-center gap-x-2 gap-y-1">
                    {tenancy.unit ? (
                      <Link
                        to="/immobilien/einheiten/$unitId"
                        params={{ unitId: String(tenancy.unit.id) }}
                        className="text-sm font-medium underline-offset-4 hover:text-(--primary-text) hover:underline"
                      >
                        {unitTitle(tenancy.unit)}
                      </Link>
                    ) : (
                      <span className="text-sm font-medium">Einheit #{tenancy.unit_id}</span>
                    )}
                    <StatusBadge domain="tenancy" status={tenancy.status} />
                    {tenancy.account_number && <span className="numeric text-xs text-muted-foreground">Konto {tenancy.account_number}</span>}
                  </span>
                  <TenantNames parties={tenancy.parties} />
                  <span className="numeric text-xs text-muted-foreground">
                    {formatDate(tenancy.starts_on)} – {tenancy.ends_on ? formatDate(tenancy.ends_on) : 'heute'}
                    {tenancy.terminated_on && ` · gekündigt am ${formatDate(tenancy.terminated_on)}`}
                  </span>
                </div>
              </li>
            ))}
          </ItemList>
          <Pager page={page} lastPage={lastPage} onPage={(next) => update({ page: next > 1 ? next : undefined })} />
        </>
      )}
    </div>
  );
}

/** "Mia Mieter, Max Mieter (Mitmieter)" @param {{ parties?: Array<{ role: string, party?: { id: number, display_name?: string|null } }> }} props */
function TenantNames({ parties }) {
  if (!parties?.length) return <span className="text-[0.8125rem] text-muted-foreground">Keine Mieter erfasst</span>;
  return (
    <ul className="flex flex-wrap gap-x-3 gap-y-0.5 text-[0.8125rem]" aria-label="Mieter">
      {parties.map((entry) => (
        <li key={`${entry.role}-${entry.party?.id}`}>
          {entry.party?.display_name ?? '–'}
          {entry.role !== 'main' && <span className="text-muted-foreground"> ({labelOf(TENANCY_PARTY_ROLE_LABELS, entry.role)})</span>}
        </li>
      ))}
    </ul>
  );
}
